import { differenceInDays } from 'date-fns';
import { isNil, keyBy } from 'lodash';

import { type EntityLookupResponse } from '@air/api';
import {
  type AssetFileKind,
  type BaseCustomField,
  CustomFieldTypeName,
  type CustomFieldWithValues,
  type SearchFilterAssetCustomFields,
  type SearchFilterDate,
} from '@air/api/types';

import { type useFilterParams } from '~/hooks/filters/useFilterParams';
import {
  type DateFilter,
  type Filters,
  type ImportedKeywordsFilterType,
  type PeopleFilterType,
  type SelectedFilter,
  type SmartTagsFilterType,
  type TagsFilterType,
} from '~/store/filters/types';
import { type FilterLookupsOptions } from '~/swr-hooks/filters/types';
import { dateStateFromDateFilter } from '~/utils/filters/dateCustomFieldFilter';
import { plainTextStateFromTextFilter } from '~/utils/filters/plainTextCustomFieldFilter';
import { isDefined } from '~/utils/isDefined';

const DEFAULT_COLOR_FILTER_THRESHOLD = 0.5;

const DAYS_IN_WEEK = 7;
const DAYS_IN_MONTH = 30;
const DAYS_IN_YEAR = 365;

export const getTagsStateFromParams = (
  tagsParam: ReturnType<typeof useFilterParams>['tagsParam'],
  tagOptions: EntityLookupResponse['tags'],
) => {
  const tags: TagsFilterType = { tags: [], logic: 'and' };
  let tagsToAdd: string[] = [];

  if (tagsParam?.and?.length) {
    tags.logic = 'and';
    tagsToAdd = tagsParam?.and;
  } else if (tagsParam?.or?.length) {
    tags.logic = 'or';
    tagsToAdd = tagsParam?.or;
  } else if (tagsParam?.not?.length) {
    tags.logic = 'not';
    tagsToAdd = tagsParam?.not;
  }

  const tagOptionsByLabel = keyBy(tagOptions, 'label');

  tags.tags = tagsToAdd.filter((label) => !!tagOptionsByLabel[label]).map((label) => tagOptionsByLabel[label]);

  return tags;
};

export const getPeopleStateFromParams = (
  peopleParam: ReturnType<typeof useFilterParams>['peopleParam'],
  peopleOptions: EntityLookupResponse['people'],
) => {
  const people: PeopleFilterType = { people: [], logic: 'and' };

  let peopleToAdd: string[] = [];

  if (peopleParam?.and?.length) {
    people.logic = 'and';
    peopleToAdd = peopleParam?.and;
  } else if (peopleParam?.or?.length) {
    people.logic = 'or';
    peopleToAdd = peopleParam?.or;
  }

  const peopleOptionsById = keyBy(peopleOptions, 'id');
  people.people = peopleToAdd.filter((id) => !!peopleOptionsById[id]).map((id) => peopleOptionsById[id]);
  return people;
};

export const getKeywordsStateFromParams = (
  keywordsParam: ReturnType<typeof useFilterParams>['importedKeywordsParam'],
) => {
  const keywords: ImportedKeywordsFilterType = { keywords: [], logic: 'and' };

  if (keywordsParam?.and?.length) {
    keywords.logic = 'and';
    keywords.keywords = keywordsParam?.and;
  } else if (keywordsParam?.or?.length) {
    keywords.logic = 'or';
    keywords.keywords = keywordsParam?.or;
  } else if (keywordsParam?.not?.length) {
    keywords.logic = 'not';
    keywords.keywords = keywordsParam?.not;
  }

  return keywords;
};

export const getUploadersStateFromParams = (
  uploadersParam: ReturnType<typeof useFilterParams>['uploadersParam'],
  uploaders: FilterLookupsOptions['uploaders'],
): Filters['uploaders'] => {
  const uploadersByValue = keyBy(uploaders, 'value');

  return uploadersParam?.or?.map((uploaderId) => uploadersByValue[uploaderId]).filter(isDefined) ?? [];
};

export const getColorsStateFromParams = (
  colorsParam: ReturnType<typeof useFilterParams>['colorsParam'],
): Filters['colors'] => {
  return {
    colors: colorsParam?.or?.map((color) => color).filter(isDefined) ?? [],
    threshold: colorsParam?.threshold ?? DEFAULT_COLOR_FILTER_THRESHOLD,
  };
};

export const getOthersStateFromParams = ({
  bookmarked,
  hasVersions,
  hasComments,
  untagged,
  isOnBoards,
}: {
  bookmarked: ReturnType<typeof useFilterParams>['bookmarkedParam'];
  isOnBoards: ReturnType<typeof useFilterParams>['isOnBoardsParam'];
  hasVersions: ReturnType<typeof useFilterParams>['hasVersionsParam'];
  hasComments: ReturnType<typeof useFilterParams>['hasOpenCommentsParam'];
  untagged: ReturnType<typeof useFilterParams>['untaggedParam'];
}): Filters['others'] => {
  const otherFilters: Filters['others'] = [];

  if (!isNil(bookmarked?.is)) {
    otherFilters.push('is-favorited');
  }

  if (!isNil(isOnBoards?.is)) {
    otherFilters.push('is-on-boards');
  }

  if (!isNil(hasVersions?.is)) {
    otherFilters.push('has-versions');
  }

  if (!isNil(hasComments)) {
    otherFilters.push('has-open-comments');
  }

  if (!isNil(untagged)) {
    otherFilters.push('is-untagged');
  }

  return otherFilters;
};

export const getTypesStateFromParams = (
  typesParam: ReturnType<typeof useFilterParams>['typesParam'],
): Filters['types'] => {
  return typesParam;
};

export const getSmartTagsStateFromParams = (
  smartTagsParam: ReturnType<typeof useFilterParams>['smartTagsParam'],
): Filters['smartTags'] => {
  const smartTags: SmartTagsFilterType = { smartTags: [], logic: 'and' };
  let smartTagsToAdd: string[] = [];

  if (smartTagsParam?.and?.length) {
    smartTags.logic = 'and';
    smartTagsToAdd = smartTagsParam?.and;
  } else if (smartTagsParam?.or?.length) {
    smartTags.logic = 'or';
    smartTagsToAdd = smartTagsParam?.or;
  } else if (smartTagsParam?.not?.length) {
    smartTags.logic = 'not';
    smartTagsToAdd = smartTagsParam?.not;
  }

  smartTags.smartTags = smartTagsToAdd.map((smartTagId) => ({
    id: smartTagId,
    label: smartTagId,
  }));

  return smartTags;
};

export const getAssetFileKindStateFromParams = (
  assetFileKindParam: ReturnType<typeof useFilterParams>['assetFileKindParam'],
): Filters['assetFileKind'] => {
  const assetFileKindArray: AssetFileKind[] = [];

  if (assetFileKindParam?.or) {
    assetFileKindArray.push(...assetFileKindParam.or);
  }
  if (assetFileKindParam?.is) {
    assetFileKindArray.push(assetFileKindParam.is);
  }

  return assetFileKindArray;
};

export const getImportedKeywordsStateFromParams = (
  importedKeywordsParam: ReturnType<typeof useFilterParams>['importedKeywordsParam'],
): Filters['importedKeywords'] => {
  const keywords: ImportedKeywordsFilterType = { keywords: [], logic: 'and' };
  let keywordsToAdd: string[] = [];

  if (importedKeywordsParam?.and?.length) {
    keywords.logic = 'and';
    keywordsToAdd = importedKeywordsParam?.and;
  } else if (importedKeywordsParam?.or?.length) {
    keywords.logic = 'or';
    keywordsToAdd = importedKeywordsParam?.or;
  } else if (importedKeywordsParam?.not?.length) {
    keywords.logic = 'not';
    keywordsToAdd = importedKeywordsParam?.not;
  }

  keywords.keywords = keywordsToAdd;

  return keywords;
};

export const getLocationStateFromParams = (locationParam: ReturnType<typeof useFilterParams>['locationParam']) => {
  const cities = locationParam?.city?.or ?? [];
  const states = locationParam?.state?.or ?? [];
  const countries = locationParam?.country?.or ?? [];

  return {
    cities,
    states,
    countries,
  };
};

export const getDateStateFromParams = (dates: SearchFilterDate | null): DateFilter | null => {
  if (!dates) {
    return null;
  }
  const { end, start } = dates;

  if (start && !end) {
    const diffInDays = differenceInDays(new Date(), new Date(start));
    switch (diffInDays) {
      case 0:
        return {
          type: 'today',
        };
      case 1:
        return {
          type: 'yesterday',
        };
      case DAYS_IN_WEEK:
        return {
          type: 'last-week',
        };
      case DAYS_IN_MONTH: {
        return {
          type: 'last-month',
        };
      }
      case DAYS_IN_YEAR: {
        return {
          type: 'last-year',
        };
      }
      default: {
        return {
          type: 'custom-range',
          startDate: start,
          endDate: end,
        };
      }
    }
  } else if (start && end) {
    const diffInDays = differenceInDays(new Date(), new Date(start));
    switch (diffInDays) {
      case 0:
        return {
          type: 'today',
        };
      case 1:
        return {
          type: 'yesterday',
        };
      default:
        return {
          type: 'custom-range',
          startDate: start,
          endDate: end,
        };
    }
  }

  return null;
};

const selectCustomFieldState = ({
  customFieldId,
  logic,
  valueIds,
  valuesById,
}: {
  customFieldId: string;
  logic: Filters['customFields'][number]['logic'];
  valueIds: string[];
  valuesById: { [key: string]: CustomFieldWithValues['values'][number] };
}): Filters['customFields'][number] | null => {
  const values = valueIds.map((valueId) => valuesById[valueId]).filter((value) => !!value);

  if (!values.length) {
    return null;
  }

  return {
    id: customFieldId,
    logic,
    values,
  };
};

export const EMPTY_CUSTOM_FIELDS: BaseCustomField[] = [];
export const UNRESOLVED_PLAIN_TEXT_FIELD_NAME = 'Custom field';

export type CustomFieldCatalogHydration =
  | { status: 'pending' }
  | { status: 'failed' }
  | { status: 'ready'; customFields: BaseCustomField[] };

export const customFieldCatalogHydration = ({
  customFields,
  isReady,
  error,
}: {
  customFields: BaseCustomField[] | undefined;
  isReady: boolean;
  error?: unknown;
}): CustomFieldCatalogHydration => {
  if (!isReady) {
    return { status: 'pending' };
  }
  if (error && !customFields) {
    return { status: 'failed' };
  }

  return { status: 'ready', customFields: customFields ?? EMPTY_CUSTOM_FIELDS };
};

export const catalogCustomFieldsForHydration = (
  hydration: CustomFieldCatalogHydration,
): BaseCustomField[] | undefined => {
  switch (hydration.status) {
    case 'ready':
      return hydration.customFields;
    case 'failed':
      return EMPTY_CUSTOM_FIELDS;
    case 'pending':
      return undefined;
    default: {
      const exhaustiveStatus: never = hydration;

      return exhaustiveStatus;
    }
  }
};

const preservedPlainTextFilter = ({
  id,
  plainText,
}: {
  id: string;
  plainText: NonNullable<Filters['customFields'][number]['plainText']>;
}): Filters['customFields'][number] => ({
  id,
  logic: 'and',
  values: [],
  plainText,
});

const preservedDateFilter = ({
  id,
  date,
}: {
  id: string;
  date: NonNullable<Filters['customFields'][number]['date']>;
}): Filters['customFields'][number] => ({
  id,
  logic: 'and',
  values: [],
  date,
});

export const customFieldsHydratedWithFailedCatalog = ({
  hydrated,
  urlFilters,
}: {
  hydrated: Filters['customFields'];
  urlFilters: SearchFilterAssetCustomFields[];
}): Filters['customFields'] => {
  const hydratedIds = new Set(hydrated.map((field) => field.id));
  const preserved = urlFilters.flatMap((field) => {
    if (hydratedIds.has(field.id)) {
      return [];
    }
    if ('text' in field.filter) {
      const plainText = plainTextStateFromTextFilter(field.filter.text);

      return plainText ? [preservedPlainTextFilter({ id: field.id, plainText })] : [];
    }
    if ('date' in field.filter) {
      const date = dateStateFromDateFilter(field.filter.date);

      return date ? [preservedDateFilter({ id: field.id, date })] : [];
    }
    if (!field.filter.notSet) {
      return [];
    }

    return [preservedPlainTextFilter({ id: field.id, plainText: { operator: 'notSet', values: [] } })];
  });

  return [...hydrated, ...preserved];
};

const unresolvedCustomFieldSelection = ({
  id,
  fieldType,
}: {
  id: string;
  fieldType: CustomFieldTypeName.date | CustomFieldTypeName.plainText;
}): SelectedFilter => ({
  type: 'customField',
  unresolvedPlainText: true,
  customField: {
    id,
    name: UNRESOLVED_PLAIN_TEXT_FIELD_NAME,
    type: { id: fieldType, title: fieldType },
  },
});

export const selectedFiltersForUnresolvedPlainText = (fields: Filters['customFields']): SelectedFilter[] =>
  fields.flatMap((field) => {
    if (field.date) {
      return [unresolvedCustomFieldSelection({ id: field.id, fieldType: CustomFieldTypeName.date })];
    }
    if (field.plainText) {
      return [unresolvedCustomFieldSelection({ id: field.id, fieldType: CustomFieldTypeName.plainText })];
    }

    return [];
  });

export const unresolvedPlainTextCustomFieldsToClear = ({
  catalogFieldIds,
  selectedFilters,
}: {
  catalogFieldIds: ReadonlySet<string>;
  selectedFilters: SelectedFilter[];
}): NonNullable<SelectedFilter['customField']>[] =>
  selectedFilters.flatMap((filter) => {
    if (filter.type !== 'customField' || !filter.customField || !filter.unresolvedPlainText) {
      return [];
    }
    if (catalogFieldIds.has(filter.customField.id)) {
      return [];
    }

    return [filter.customField];
  });

export const plainTextSelectionNeedsCatalog = ({
  catalogFields,
  customFields,
  selectedCustomFields,
}: {
  catalogFields: BaseCustomField[];
  customFields: Filters['customFields'] | undefined;
  selectedCustomFields: { id: string; name: string; unresolvedPlainText?: boolean }[];
}) => {
  const catalogById = keyBy(catalogFields, 'id');
  const selectedById = keyBy(selectedCustomFields, 'id');

  return (customFields ?? []).some((field) => {
    const catalogField = catalogById[field.id];

    if (!catalogField) {
      return false;
    }

    const selected = selectedById[field.id];
    const selectionIsStale = !selected || !!selected.unresolvedPlainText || selected.name !== catalogField.name;

    if (field.date) {
      return catalogField.type.title === CustomFieldTypeName.date && selectionIsStale;
    }
    if (!field.plainText) {
      return false;
    }
    if (catalogField.type.title === CustomFieldTypeName.date) {
      return true;
    }
    if (catalogField.type.title !== CustomFieldTypeName.plainText) {
      return false;
    }

    return selectionIsStale;
  });
};

export const mergeCustomFieldsForHydration = ({
  lookupFields,
  catalogFields,
}: {
  lookupFields: CustomFieldWithValues[];
  catalogFields: BaseCustomField[];
}) => {
  const catalogWithValues: CustomFieldWithValues[] = catalogFields.map((field) => ({
    id: field.id,
    name: field.name,
    type: field.type,
    values: [],
  }));

  return keyBy([...catalogWithValues, ...lookupFields], 'id');
};

export const entityLookupWithCatalogCustomFields = ({
  entities,
  catalogFields,
}: {
  entities: EntityLookupResponse;
  catalogFields: BaseCustomField[];
}): EntityLookupResponse => ({
  ...entities,
  customFields: Object.values(
    mergeCustomFieldsForHydration({
      lookupFields: entities.customFields,
      catalogFields,
    }),
  ),
});

export const getCustomFieldsStateFromParams = (
  customFieldsParam: SearchFilterAssetCustomFields[],
  customFieldsById: { [key: string]: CustomFieldWithValues },
): Filters['customFields'] => {
  return customFieldsParam.reduce<Filters['customFields']>((acc, curr) => {
    if ('text' in curr.filter) {
      if (customFieldsById[curr.id]?.type.title === CustomFieldTypeName.plainText) {
        const plainText = plainTextStateFromTextFilter(curr.filter.text);

        if (plainText) {
          acc.push({
            id: curr.id,
            logic: 'and',
            values: [],
            plainText,
          });
        }
      }

      return acc;
    }

    if ('date' in curr.filter) {
      if (customFieldsById[curr.id]?.type.title === CustomFieldTypeName.date) {
        const date = dateStateFromDateFilter(curr.filter.date);

        if (date) {
          acc.push({
            id: curr.id,
            logic: 'and',
            values: [],
            date,
          });
        }
      }

      return acc;
    }

    if (curr.filter.notSet && customFieldsById[curr.id]?.type.title === CustomFieldTypeName.plainText) {
      acc.push({
        id: curr.id,
        logic: 'and',
        values: [],
        plainText: { operator: 'notSet', values: [] },
      });
      return acc;
    }

    if (curr.filter.notSet && customFieldsById[curr.id]?.type.title === CustomFieldTypeName.date) {
      acc.push({
        id: curr.id,
        logic: 'and',
        values: [],
        date: { operator: 'notSet' },
      });
      return acc;
    }

    const valuesById = keyBy(customFieldsById[curr.id]?.values ?? [], 'id');

    if (curr.filter.and?.length) {
      const selectState = selectCustomFieldState({
        customFieldId: curr.id,
        logic: 'and',
        valueIds: curr.filter.and,
        valuesById,
      });

      if (selectState) {
        acc.push(selectState);
      }

      return acc;
    }
    if (curr.filter.or?.length) {
      const selectState = selectCustomFieldState({
        customFieldId: curr.id,
        logic: 'or',
        valueIds: curr.filter.or,
        valuesById,
      });

      if (selectState) {
        acc.push(selectState);
      }

      return acc;
    }
    if (curr.filter.not?.length) {
      const selectState = selectCustomFieldState({
        customFieldId: curr.id,
        logic: 'not',
        valueIds: curr.filter.not,
        valuesById,
      });

      if (selectState) {
        acc.push(selectState);
      }
    }

    return acc;
  }, []);
};

export { customFieldIdFromUrlParam } from '~/utils/filters/plainTextCustomFieldFilter';

export const getLocationsStateFromParams = (locationsParam: ReturnType<typeof useFilterParams>['locationsParam']) => {
  const boards = locationsParam?.boards;
  const libraries = locationsParam?.libraries;
  const boardsToAdd = [];
  const librariesToAdd = [];

  if (boards?.or?.length) {
    boardsToAdd.push(...boards.or);
  }

  if (libraries?.or?.length) {
    librariesToAdd.push(...libraries.or);
  }

  return {
    boards: boardsToAdd,
    libraries: librariesToAdd,
  };
};
